/**
 * Step 2: choose boarding/loading and arrival/unloading visits, grouped or individually.
 *
 * @remarks
 * Choice UI delegates changes to the review state hook; it does not edit the route
 * directly. Journey grouping and assignment calculations live in state/journeys.ts.
 *
 * @packageDocumentation
 */
import React from 'react';
import {Alert, Button, Icon, Radio, Tabs, Tag, Typography} from 'antd';
import {
    AssignmentRow,
    VisitRange,
    assignmentKey,
    rangeIndexes,
} from '../../../../../../common/flight-leg/assignment-plan';
import {RouteVisit} from '../../../../../../common/flight-leg/route-visits';
import JourneyEndpoint from '../../shared/journey-endpoint';
import {suggestedCandidate} from '../../state/journeys';
import {visitLabel} from '../../state/itinerary';
import type {AutoAssignReview} from '../../state/use-auto-assign-review';

/** State and display names needed to render visit decisions. */
export interface VisitChoicesStepProps {
    flow: Pick<
        AutoAssignReview,
        | 'plan'
        | 'groups'
        | 'selectionView'
        | 'extend'
        | 'choiceFor'
        | 'choose'
        | 'repeatedLocations'
        | 'setSelectionView'
        | 'isMixed'
    >;
    names: Map<string, string>;
    labels: Map<string, string>;
}

const itemLabel = (kind: 'pax' | 'cgo', count: number) =>
    kind === 'pax'
        ? count === 1
            ? 'passenger'
            : 'passengers'
        : count === 1
        ? 'cargo item'
        : 'cargo items';

/** Renders all affected passenger/cargo journeys without requiring per-group confirmation. */
export default function VisitChoicesStep({flow, names, labels}: VisitChoicesStepProps) {
    const {plan, groups, selectionView, extend, choiceFor, choose} = flow;
    const locationName = (id: string) => names.get(id) || id;
    const entityName = (row: AssignmentRow) => labels.get(assignmentKey(row)) || row.id;
    const repeated = flow.repeatedLocations;
    const visitAlert =
        repeated.length === 1
            ? `This flight visits ${locationName(repeated[0])} more than once.`
            : repeated.length > 1
            ? 'Some locations are visited more than once.'
            : 'Choose visits for each journey.';

    /** Describes an assigned visit pair, including occurrence labels for repeated locations. */
    function rangeLabel(range: VisitRange) {
        const label = (visit: RouteVisit) =>
            `${locationName(visit.locationID)}${
                visitLabel(plan, visit.key) ? ` — ${visitLabel(plan, visit.key).toLowerCase()}` : ''
            }`;
        return `${label(range.from)} → ${label(range.to)}`;
    }

    /** Explains each member's selection when grouped passengers/cargo have different choices. */
    function selectionLabel(row: AssignmentRow) {
        return choiceFor(row) === 'extend'
            ? 'New direct journey'
            : row.status === 'review'
            ? 'No visit selected'
            : row.status === 'unassigned'
            ? 'Unassigned'
            : row.ranges.map(rangeLabel).join('; ');
    }

    /**
     * Names the endpoint choices that differ across the available journeys.
     *
     * @remarks
     * Both endpoints are named when both vary; otherwise only the variable endpoint
     * is emphasized. Occurrence labels distinguish visits without using leg numbers.
     */
    function optionLabel(row: AssignmentRow, range: VisitRange) {
        const fromVaries = new Set(row.candidates.map((candidate) => candidate.from.key)).size > 1;
        const toVaries = new Set(row.candidates.map((candidate) => candidate.to.key)).size > 1;
        const label = (visit: RouteVisit, boarding: boolean) =>
            `${
                row.kind === 'pax'
                    ? boarding
                        ? 'Board at'
                        : 'Leave at'
                    : boarding
                    ? 'Load at'
                    : 'Unload at'
            } ${locationName(visit.locationID)}${
                visitLabel(plan, visit.key) ? ` — ${visitLabel(plan, visit.key).toLowerCase()}` : ''
            }`;
        if (fromVaries && toVaries) return `${label(range.from, true)}; ${label(range.to, false)}`;
        return fromVaries ? label(range.from, true) : label(range.to, false);
    }

    /**
     * Renders one set of choices for a journey group or an individual assignment.
     *
     * @remarks
     * Mixed selections leave the group radio unset. The shortest journey is marked
     * as suggested without being confirmed; choosing a new direct journey records
     * intent until the user continues to final review.
     */
    function visitOptions(rows: AssignmentRow[]) {
        const row = rows[0];
        const choices = rows.map(choiceFor);
        const uniform = choices.every((value) => value === choices[0]);
        const selected = uniform ? choices[0] : undefined;
        const suggested = suggestedCandidate(plan, row);
        const subject =
            rows.length === 1
                ? entityName(row)
                : `all ${rows.length} ${itemLabel(row.kind, rows.length)}`;
        return (
            <fieldset className="journey-visits">
                <legend>
                    {row.kind === 'pax'
                        ? 'Boarding and arrival visits'
                        : 'Loading and unloading visits'}
                </legend>
                {!row.candidates.length && (
                    <Typography.Paragraph type="secondary">
                        This route has no valid journey for {subject}.
                    </Typography.Paragraph>
                )}
                <Radio.Group
                    aria-label={`Visits for ${subject}`}
                    value={selected}
                    onChange={(event) => choose(rows, event.target.value)}
                    className="visit-options"
                >
                    {row.candidates.map((range, index) => {
                        const [from, to] = rangeIndexes(plan.visits, range);
                        return (
                            <Radio value={index} key={`${range.from.key}:${range.to.key}`}>
                                <span className="visit-option__body">
                                    <span>
                                        <Typography.Text strong>
                                            {optionLabel(row, range)}
                                        </Typography.Text>
                                        {index === suggested && <Tag>Suggested</Tag>}
                                    </span>
                                    <Typography.Text type="secondary">
                                        {plan.visits.slice(from, to + 1).map((visit, i, visits) => (
                                            <React.Fragment key={visit.key}>
                                                {i > 0 && ' → '}
                                                {i === 0 || i === visits.length - 1 ? (
                                                    <JourneyEndpoint
                                                        plan={plan}
                                                        names={names}
                                                        visit={visit}
                                                        kind={row.kind}
                                                        boarding={i === 0}
                                                    />
                                                ) : (
                                                    locationName(visit.locationID)
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Typography.Text>
                                </span>
                            </Radio>
                        );
                    })}
                    {extend && (
                        <Radio value="extend">
                            <span className="visit-option__body">
                                <Typography.Text strong>Add a new direct journey</Typography.Text>
                                <Typography.Text type="secondary">
                                    When you continue, add boarding/loading and arrival visits
                                    before the final return, if there is one.
                                </Typography.Text>
                            </span>
                        </Radio>
                    )}
                    <Radio value="unassign">
                        Leave{' '}
                        {rows.length === 1
                            ? `this ${itemLabel(row.kind, 1)}`
                            : `these ${rows.length} ${itemLabel(row.kind, rows.length)}`}{' '}
                        unassigned
                    </Radio>
                </Radio.Group>
            </fieldset>
        );
    }
    return (
        <>
            <Alert
                type="info"
                showIcon
                message={visitAlert}
                description="Choose the boarding/loading and arrival/unloading visits for each journey."
            />
            <Tabs
                activeKey={selectionView}
                onChange={flow.setSelectionView}
                className="selection-view-tabs"
            >
                <Tabs.TabPane tab="By group" key="groups" />
                <Tabs.TabPane tab="Individually" key="individual" />
            </Tabs>
            {groups.map((rows) => {
                const row = rows[0];
                const entity = (row.kind === 'pax' ? plan.pax : plan.cargo).find(
                    (entity) => entity._id === row.id
                );
                const routeLabel = `${locationName(entity.departureID)} to ${locationName(
                    entity.destinationID
                )}`;
                const mixed = flow.isMixed(rows);
                return (
                    <section className="journey-section" key={rows.map(assignmentKey).join('|')}>
                        <div className="journey-heading">
                            <Typography.Title level={4}>
                                <Icon type={row.kind === 'pax' ? 'user' : 'inbox'} /> {routeLabel}
                            </Typography.Title>
                        </div>
                        {selectionView === 'groups' ? (
                            <div className="journey-layout">
                                <div className="journey-passengers">
                                    <Typography.Text type="secondary">
                                        {row.kind === 'pax' ? 'Passengers' : 'Cargo'} ({rows.length}
                                        )
                                    </Typography.Text>
                                    <ul
                                        aria-label={`${
                                            row.kind === 'pax' ? 'Passengers' : 'Cargo'
                                        } travelling ${routeLabel}`}
                                    >
                                        {rows.map((item) => (
                                            <li key={assignmentKey(item)}>
                                                <Icon
                                                    type={row.kind === 'pax' ? 'user' : 'inbox'}
                                                />
                                                <span>
                                                    {entityName(item)}
                                                    {mixed && (
                                                        <Typography.Text
                                                            type="secondary"
                                                            className="passenger-visit-label"
                                                        >
                                                            {selectionLabel(item)}
                                                        </Typography.Text>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    {mixed && (
                                        <div className="mixed-choice-note">
                                            <Typography.Text type="secondary">
                                                These selections differ. A group choice replaces
                                                every selection listed.
                                            </Typography.Text>
                                            <Button
                                                type="link"
                                                onClick={() => flow.setSelectionView('individual')}
                                            >
                                                Edit individually
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {visitOptions(rows)}
                            </div>
                        ) : (
                            rows.map((item) => (
                                <div
                                    className="journey-layout individual-journey"
                                    key={assignmentKey(item)}
                                >
                                    <div className="journey-passengers">
                                        <Typography.Text strong>
                                            <Icon type={item.kind === 'pax' ? 'user' : 'inbox'} />{' '}
                                            {entityName(item)}
                                        </Typography.Text>
                                        <Typography.Text
                                            type="secondary"
                                            className="passenger-visit-label"
                                        >
                                            {selectionLabel(item)}
                                        </Typography.Text>
                                    </div>
                                    {visitOptions([item])}
                                </div>
                            ))
                        )}
                    </section>
                );
            })}
        </>
    );
}
